import { useEffect } from "react";

import { authApi } from "@/lib/api/auth";
import { queryClient } from "@/lib/query-client";
import { notify } from "@/lib/notify";
import { revokedSessionFrom, sessionIdOf } from "@/lib/session/revokedSession";
import { openSessionEndedStream } from "@/lib/session/sessionEvents";
import { useAuthStore } from "@/stores/auth-store";

export const REVOKED_SIGN_OUT_MESSAGE = "This device was signed out from another device. Please log in again.";

/** Asks the server about the session. A dead one answers 401, which the API
 * client turns into the usual sign-out (message, other tabs told, login page). */
function checkSession() {
  // Errors are handled by the API client; nothing to do here.
  authApi.sessionStatus().catch(() => undefined);
}

/** A Super Admin switched a module on or off: re-read the company's features
 * (sidebar, module pages, the Module Access page) so they follow at once, and
 * reload what is on screen - a page whose module was just switched on had
 * been refused until now. Toggles are rare, so reloading everything is cheap. */
function reloadFeatures() {
  void queryClient.invalidateQueries();
}

/**
 * Signs this tab out the moment its session is ended somewhere else - another
 * device's "Log Out", an administrator, a password change, the idle timeout.
 *
 * 1. A live stream from the server (openSessionEndedStream) says "ended" as it
 *    happens, so the tab leaves at once with nobody having to click.
 * 2. Another tab of this browser learned the session ended and says so: if it
 *    is the session this tab holds, sign this tab out too.
 * 3. When the tab comes back into view, ask the server, in case the stream
 *    was interrupted while the computer slept.
 *
 * All three are only ever hints: the server refuses a revoked session on every
 * request regardless.
 */
export function RevokedSessionSync() {
  const sessionId = useAuthStore((s) => sessionIdOf(s.accessToken));

  useEffect(() => {
    if (!sessionId) return;
    return openSessionEndedStream({ onEnded: checkSession, onUnauthorized: checkSession, onFeaturesChanged: reloadFeatures });
  }, [sessionId]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      const revoked = revokedSessionFrom(event);
      if (!revoked) return;
      const mine = sessionIdOf(useAuthStore.getState().accessToken);
      if (mine && mine === revoked) {
        notify.error(REVOKED_SIGN_OUT_MESSAGE);
        useAuthStore.getState().signOut();
        queryClient.clear();
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible" && useAuthStore.getState().accessToken) checkSession();
    };
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
  return null;
}
